// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Local-only implementation of useAnyCollection backed by RxDB.
 *
 * Queries either a native RxDB collection or a virtual schema-in-db
 * collection transparently. Extracted from useAnyCollection for mode
 * routing — this file contains the pure RxDB logic.
 */

import { useEffect, useState } from 'react'
import { SchemaRegistry } from '../../../core/SchemaRegistry'
import { VirtualCollectionAdapter } from '../../../core/VirtualCollectionAdapter'
import type { RecordModel } from '../../../models/Record'

export interface UseLocalAnyCollectionOptions {
  /** MongoDB-style selector for filtering */
  selector?: Record<string, unknown>
  /** Sort criteria array, e.g. [{ title: 'asc' }] */
  sort?: Array<Record<string, 'asc' | 'desc'>>
  /** Maximum number of results */
  limit?: number
  /** Number of results to skip */
  skip?: number
  /** Organization ID (needed for virtual collection schema loading) */
  orgId?: string
}

export interface UseLocalAnyCollectionResult<T> {
  data: T[]
  isLoading: boolean
  error: Error | null
}

/**
 * Minimal structural type covering the subset of RxDatabase used by this hook.
 * Accepts both native (db.collections) and virtual (db.schemas + db.records) shapes.
 */
export interface AnyDb {
  /** Native RxDB collections keyed by name */
  collections?: Record<string, NativeCollection>
  /** Schema collection for virtual collection support */
  schemas?: unknown
  /** Records collection for virtual collection support */
  records?: unknown
}

/** Minimal RxDB document type for native collection results */
interface RxDoc {
  toJSON(): Record<string, unknown>
}

/** Minimal native RxDB collection interface used by this hook */
interface NativeCollection {
  find(q: {
    selector: Record<string, unknown>
    sort?: Array<Record<string, 'asc' | 'desc'>>
  }): NativeQuery
}

interface NativeQuery {
  skip(n: number): NativeQuery
  limit(n: number): NativeQuery
  $: {
    subscribe(observer: { next: (docs: RxDoc[]) => void; error: (err: Error) => void }): {
      unsubscribe: () => void
    }
  }
}

// Module-level cache: one SchemaRegistry per db instance (separate from useRecords cache)
const registryCache = new WeakMap<object, SchemaRegistry>()

function getOrCreateRegistry(db: { schemas: unknown; records: unknown }): SchemaRegistry {
  let registry = registryCache.get(db as object)
  if (!registry) {
    registry = new SchemaRegistry(
      db.schemas as ConstructorParameters<typeof SchemaRegistry>[0],
      db.records as ConstructorParameters<typeof SchemaRegistry>[1]
    )
    registryCache.set(db as object, registry)
  }
  return registry
}

const EMPTY_DATA: never[] = []

/**
 * React hook that queries either a native RxDB collection or a virtual
 * schema-in-db collection transparently (local path only).
 *
 * - If `collectionName` exists in `db.collections`, queries the native collection.
 * - Otherwise, falls back to SchemaRegistry + VirtualCollectionAdapter (records table).
 *
 * This is a read-only query hook. For CRUD on virtual collections, use `useRecords`.
 *
 * @param db - RxDatabase instance (or null/undefined)
 * @param collectionName - Collection name (native or virtual)
 * @param options - Query options: selector, sort, limit, skip, orgId
 * @returns Reactive data array with loading/error state
 */
export function useLocalAnyCollection<T = Record<string, unknown>>(
  db: AnyDb | null | undefined,
  collectionName: string,
  options: UseLocalAnyCollectionOptions = {}
): UseLocalAnyCollectionResult<T> {
  const [data, setData] = useState<T[]>(EMPTY_DATA)
  const [isLoading, setIsLoading] = useState(db !== null && db !== undefined)
  const [error, setError] = useState<Error | null>(null)

  // Serialize options for dependency comparison
  const selectorKey = JSON.stringify(options.selector ?? {})
  const sortKey = JSON.stringify(options.sort ?? [])
  const { limit, skip, orgId } = options

  useEffect(() => {
    if (!db) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset state when db is unavailable
      setData(EMPTY_DATA)
      setIsLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    let subscription: { unsubscribe: () => void } | null = null

    // Check if this is a native RxDB collection
    const nativeCollection = db.collections?.[collectionName]

    if (nativeCollection) {
      // === NATIVE COLLECTION PATH ===
      setIsLoading(true)
      setError(null)

      try {
        const selector = options.selector ?? {}
        const findOptions: {
          selector: Record<string, unknown>
          sort?: Array<Record<string, 'asc' | 'desc'>>
        } = { selector }
        if (options.sort && options.sort.length > 0) {
          findOptions.sort = options.sort
        }

        let query = nativeCollection.find(findOptions)
        if (skip) query = query.skip(skip)
        if (limit) query = query.limit(limit)

        subscription = query.$.subscribe({
          next: (docs: RxDoc[]) => {
            if (cancelled) return
            const mapped = docs.map(d => d.toJSON() as T)
            setData(mapped)
            setIsLoading(false)
          },
          error: (err: Error) => {
            if (cancelled) return
            setError(err)
            setIsLoading(false)
          },
        })
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
          setIsLoading(false)
        }
      }
    } else if (db.schemas && db.records) {
      // === VIRTUAL COLLECTION PATH (schema-in-db) ===
      // Arrow expression avoids the no-inner-declarations ESLint rule that
      // prohibits function declarations inside block scopes.
      const initVirtual = async (): Promise<void> => {
        try {
          setIsLoading(true)
          setError(null)

          const registry = getOrCreateRegistry(db as { schemas: unknown; records: unknown })

          // Load schemas if registry doesn't have this schema yet
          if (!registry.hasSchema(collectionName)) {
            await registry.loadSchemas(orgId ?? '')
          }

          const adapter = registry.getAdapter(collectionName)
          if (!adapter) {
            if (!cancelled) {
              // Not a native collection and not a virtual schema — genuinely missing
              setData(EMPTY_DATA)
              setIsLoading(false)
            }
            return
          }

          const selector = options.selector ?? {}
          const rewrittenSelector = adapter.rewriteSelector(selector)
          const dataFilter = adapter.extractDataFilter(selector)
          const hasDataFilter = Object.keys(dataFilter).length > 0

          // Split sort into RxDB-safe and client-side parts
          const sortOpts = options.sort ?? []
          const dbSort = adapter.rewriteSort(sortOpts)
          const clientSort = adapter.extractClientSort(sortOpts)

          const records = db.records as {
            find(q: { selector: Record<string, unknown>; sort?: Array<Record<string, string>> }): {
              skip(n: number): unknown
              limit(n: number): unknown
              $: {
                subscribe(fn: { next: (docs: unknown[]) => void; error: (err: Error) => void }): {
                  unsubscribe: () => void
                }
              }
            }
          }

          const findOptions: {
            selector: Record<string, unknown>
            sort?: Array<Record<string, string>>
          } = { selector: rewrittenSelector }
          if (dbSort.length > 0) {
            findOptions.sort = dbSort
          }

          let query = records.find(findOptions) as {
            skip(n: number): unknown
            limit(n: number): unknown
            $: {
              subscribe(fn: { next: (docs: unknown[]) => void; error: (err: Error) => void }): {
                unsubscribe: () => void
              }
            }
          }

          if (skip && !hasDataFilter) query = query.skip(skip) as typeof query
          if (limit && !hasDataFilter) query = query.limit(limit) as typeof query

          subscription = query.$.subscribe({
            next: (docs: unknown[]) => {
              if (cancelled) return
              let mapped = docs.map(
                doc =>
                  adapter.mapFromRecord(
                    (
                      doc as { toJSON(): Record<string, unknown> }
                    ).toJSON() as unknown as RecordModel
                  ) as unknown as T
              )

              // Apply client-side data filter for data-bag fields
              if (hasDataFilter) {
                mapped = mapped.filter(doc =>
                  VirtualCollectionAdapter.matchesDataFilter(
                    doc as unknown as Record<string, unknown>,
                    dataFilter
                  )
                )
              }

              // Apply client-side sort for data-bag fields
              if (clientSort.length > 0) {
                mapped = VirtualCollectionAdapter.applyClientSort(
                  mapped as unknown as Record<string, unknown>[],
                  clientSort
                ) as unknown as T[]
              }

              // Apply skip/limit after client-side processing if needed
              if (hasDataFilter) {
                if (skip) mapped = mapped.slice(skip)
                if (limit) mapped = mapped.slice(0, limit)
              }

              setData(mapped)
              setIsLoading(false)
            },
            error: (err: Error) => {
              if (cancelled) return
              setError(err)
              setIsLoading(false)
            },
          })
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err : new Error(String(err)))
            setIsLoading(false)
          }
        }
      }

      initVirtual()
    } else {
      // No native collection and no schema-in-db support
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset state for unsupported collection
      setData(EMPTY_DATA)
      setIsLoading(false)
      setError(null)
    }

    return () => {
      cancelled = true
      subscription?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selector/sort serialized as selectorKey/sortKey
  }, [db, collectionName, selectorKey, sortKey, limit, skip, orgId])

  return { data, isLoading, error }
}
